/**
 * Venue pricing — internal calculator for the venue offer and Pilot → convert → expand.
 *
 * Steady-state rates match the customer Billing page: lease $95/screen/month or buy
 * $1,000/screen, install $1,000 per install trip, software $40/month or $384/year.
 *
 * The pilot package is the sales motion from WAT-33 (~$3,000 / 60-day package with
 * loaned hardware and software $0). Installation is billed per Watchly trip to the
 * venue (not “once per location forever”). The pilot trip’s install is included in
 * the package; a later return trip to add devices bills the install fee again.
 * Additional go-forward locations each bill one install-trip fee at conversion.
 * Conversion never re-bills the pilot install trip. Conversion credit does not cover
 * a return-trip or additional-location install fee.
 *
 * Converting at the end of the pilot starts a regular 12-month contract. The conversion
 * invoice bills that full term (12 months of lease if leasing, plus software for the
 * term — a year if billed yearly, or 12 months if billed monthly), not a single month.
 * The next 12-month term is billed at each anniversary of conversion.
 */

export type HardwareMode = 'lease' | 'purchase';
export type SoftwareCadence = 'month' | 'year';
export type ExpansionInstallTiming = 'during_pilot' | 'return_trip';

export type BreakEven = { kind: 'immediate' } | { kind: 'months'; monthsAfterStart: number } | { kind: 'never' };

export type VenuePricingParams = {
    /** Cost of one device to Watchly (COGS). */
    deviceHardwareCost: number;
    /** Watchly labor to install one device. */
    deviceInstallationLaborCost: number;

    hardwareMode: HardwareMode;
    /** Customer lease price per device per month. */
    hardwareLeasePerMonth: number;
    /** Customer purchase price per device (one-time). */
    hardwarePurchase: number;
    /**
     * Customer fee per Watchly install trip. Covers any number of devices installed on
     * that visit. Pilot trip is included in the package; return trips bill this again.
     */
    installationFeePerTrip: number;
    softwareCadence: SoftwareCadence;
    /** Customer software subscription per device per month. */
    softwareMonthly: number;
    /** Customer software subscription per device per year. */
    softwareYearly: number;

    /** 0–1 discount on the selected hardware option (lease or purchase). */
    hardwareDiscountFraction: number;
    /** 0–1 discount on installation. */
    installationDiscountFraction: number;
    /** 0–1 discount on software. */
    softwareDiscountFraction: number;

    pilotDurationDays: number;
    pilotDeviceCount: number;
    pilotPackagePrice: number;
    /** Dollars credited against the conversion invoice (typically the package price). */
    conversionCredit: number;
    /** Devices after conversion; must be ≥ pilotDeviceCount. Extras are expansion devices. */
    goForwardDeviceCount: number;
    /**
     * When extras are installed: during the pilot visit (no second customer install fee) or
     * a later return trip (customer pays another install-trip fee; Watchly also pays labor
     * again on the delta).
     */
    expansionInstallTiming: ExpansionInstallTiming;
    /**
     * Extra venues standing up at conversion (beyond the pilot location). Each one bills
     * the customer the install-trip fee at conversion. Conversion credit does not cover it.
     */
    additionalGoForwardLocations: number;
};

export const DEFAULT_VENUE_PRICING: VenuePricingParams = {
    deviceHardwareCost: 400,
    deviceInstallationLaborCost: 50,
    hardwareMode: 'lease',
    hardwareLeasePerMonth: 35,
    hardwarePurchase: 450,
    installationFeePerTrip: 1500,
    softwareCadence: 'year',
    softwareMonthly: 50,
    softwareYearly: 600,
    hardwareDiscountFraction: 0,
    installationDiscountFraction: 0,
    softwareDiscountFraction: 0.2,
    pilotDurationDays: 90,
    pilotDeviceCount: 4,
    pilotPackagePrice: 3000,
    conversionCredit: 3000,
    goForwardDeviceCount: 4,
    expansionInstallTiming: 'during_pilot',
    additionalGoForwardLocations: 0,
};

/** Steady-state quote with no pilot package (Billing-page worked example). */
export const STEADY_STATE_VENUE_PRICING: VenuePricingParams = {
    ...DEFAULT_VENUE_PRICING,
    pilotDurationDays: 0,
    pilotPackagePrice: 0,
    conversionCredit: 0,
    goForwardDeviceCount: 3,
    pilotDeviceCount: 3,
};

function clampFraction(n: number): number {
    if (!Number.isFinite(n)) return 0;
    return Math.min(1, Math.max(0, n));
}

function money(n: number): number {
    return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

function nonNegInt(n: number): number {
    return Math.max(0, Math.floor(Number(n)) || 0);
}

function afterDiscount(list: number, fraction: number): number {
    return money(list * (1 - clampFraction(fraction)));
}

/**
 * Apply credit to line items in order; returns remaining amounts after credit and leftover credit.
 */
function applyCreditInOrder(
    lines: { key: string; amount: number }[],
    credit: number
): { afterCredit: Record<string, number>; creditApplied: number; creditLeftover: number } {
    let remaining = money(Math.max(0, credit));
    const afterCredit: Record<string, number> = {};
    let applied = 0;
    for (const line of lines) {
        const take = money(Math.min(remaining, Math.max(0, line.amount)));
        afterCredit[line.key] = money(line.amount - take);
        remaining = money(remaining - take);
        applied = money(applied + take);
    }
    return { afterCredit, creditApplied: applied, creditLeftover: remaining };
}

export type VenueLineItem = {
    label: string;
    quantity: number;
    listUnit: number;
    discountFraction: number;
    amountBeforeCredit: number;
    amountAfterCredit: number;
};

export type CustomerInvoiceId = 'signup' | 'conversion' | 'ongoing';

export type CustomerInvoice = {
    id: CustomerInvoiceId;
    title: string;
    description: string;
    lineItems: VenueLineItem[];
    creditApplied: number;
    creditLeftover: number;
    total: number;
};

export type VenueScenarioColumn = {
    id: 'walk_away' | 'convert' | 'convert_and_expand';
    label: string;
    customerCashThroughYearOne: number;
    watchlyNetCashThroughYearOne: number;
    mrrAfterDecision: number;
    deviceCount: number;
};

export type WatchlyNetProfitPoint = {
    /** Months since signed pilot / start (0 = install day). */
    month: number;
    /** Cumulative customer cash received minus cumulative Watchly deploy spend. */
    netProfit: number;
};

export const NET_PROFIT_HORIZON_MONTHS = 60;

export type VenuePricingBreakdown = {
    pilotDeviceCount: number;
    goForwardDeviceCount: number;
    expansionDeviceCount: number;
    venueMonthsInYearOne: number;

    discountedLeasePerDevice: number;
    discountedPurchasePerDevice: number;
    discountedInstall: number;
    discountedSoftwarePeriodPerDevice: number;
    discountedSoftwareMonthlyEquivalentPerDevice: number;

    /** Gross Watchly deploy spend for go-forward devices (+ return-trip labor on extras if selected). */
    grossDeploySpend: number;
    /**
     * Capital still on Watchly after the pilot: gross deploy spend minus the pilot package
     * and conversion cash that offsets hardware/install (not software or lease).
     */
    upFrontCapitalStillOnWatchly: number;
    /**
     * Net cash stuck in the field during the pilot: (hardware + labor) × pilot devices
     * minus the package collected at signup. Lease and software do not offset.
     */
    capitalTiedUpDuringPilot: number;
    breakEvenFromStart: BreakEven;

    mrr: number;
    arr: number;

    incrementalNextScreen: {
        customerUpFront: number;
        customerMonthlyRecurring: number;
        watchlyCost: number;
        monthsToRecover: BreakEven;
    };

    pilotPackageDue: number;
    conversionBeforeCredit: {
        install: number;
        hardwarePurchase: number;
        /** First 12-month software term (yearly rate, or 12 × monthly). */
        firstSoftwarePeriod: number;
        /** First 12-month hardware lease term (not a single month). */
        firstLeaseMonth: number;
        total: number;
    };
    conversionAfterCredit: {
        install: number;
        hardwarePurchase: number;
        /** First 12-month software term after conversion credit. */
        firstSoftwarePeriod: number;
        /** First 12-month hardware lease term after conversion credit. */
        firstLeaseMonth: number;
        total: number;
        creditApplied: number;
        creditLeftover: number;
    };
    /** Expansion hardware due (purchase of extras, or 12-month lease on extras if leasing). */
    expansionHardwareDue: number;
    /**
     * Customer install-trip fee when extras are installed on a return trip (not during pilot).
     * Conversion credit does not apply to this line.
     */
    expansionInstallTripDue: number;
    recurringMonthly: number;
    recurringSoftwarePeriod: number;
    recurringSoftwarePeriodLabel: 'month' | 'year';

    /**
     * Signup, conversion (pilot only), and the next software-renewal invoice a customer
     * would actually receive. Ongoing is one software period (month or year) plus that
     * month's lease if leasing.
     */
    invoices: {
        signup: CustomerInvoice;
        /** Present only when there is a pilot window (duration > 0). */
        conversion: CustomerInvoice | null;
        ongoing: CustomerInvoice;
    };

    yearOneCustomerTotal: number;
    yearTwoCustomerTotal: number;

    scenarios: VenueScenarioColumn[];

    /** Cumulative Watchly net profit by month for the go-forward path (0…60). */
    watchlyNetProfitSeries: WatchlyNetProfitPoint[];
};

export const CONTRACT_TERM_MONTHS = 12;

function softwarePeriodList(params: VenuePricingParams): number {
    return params.softwareCadence === 'year' ? params.softwareYearly : params.softwareMonthly;
}

function discountedSoftwarePeriod(params: VenuePricingParams): number {
    return afterDiscount(softwarePeriodList(params), params.softwareDiscountFraction);
}

function discountedSoftwareMonthlyEquivalent(params: VenuePricingParams): number {
    const period = discountedSoftwarePeriod(params);
    return params.softwareCadence === 'year' ? money(period / 12) : period;
}

/** Per-device list price for one 12-month software term (yearly rate, or monthly × 12). */
function softwareContractTermListUnit(params: VenuePricingParams): number {
    return params.softwareCadence === 'year'
        ? params.softwareYearly
        : money(params.softwareMonthly * CONTRACT_TERM_MONTHS);
}

/** Per-device discounted price for one 12-month software term. */
function discountedSoftwareContractTerm(params: VenuePricingParams): number {
    const period = discountedSoftwarePeriod(params);
    return params.softwareCadence === 'year' ? period : money(period * CONTRACT_TERM_MONTHS);
}

function discountedLease(params: VenuePricingParams): number {
    return afterDiscount(params.hardwareLeasePerMonth, params.hardwareDiscountFraction);
}

function leaseContractTermListUnit(params: VenuePricingParams): number {
    return money(params.hardwareLeasePerMonth * CONTRACT_TERM_MONTHS);
}

function discountedLeaseContractTerm(params: VenuePricingParams): number {
    return money(discountedLease(params) * CONTRACT_TERM_MONTHS);
}

function discountedPurchase(params: VenuePricingParams): number {
    return afterDiscount(params.hardwarePurchase, params.hardwareDiscountFraction);
}

function discountedInstall(params: VenuePricingParams): number {
    return afterDiscount(params.installationFeePerTrip, params.installationDiscountFraction);
}

/**
 * Customer install-trip fee for expansion devices on a return visit to the pilot location.
 * Not applied when extras go in during the pilot trip; not covered by conversion credit.
 */
function returnTripInstallFee(params: VenuePricingParams, expansionDeviceCount: number): number {
    if (nonNegInt(expansionDeviceCount) <= 0) return 0;
    if (params.expansionInstallTiming !== 'return_trip') return 0;
    return discountedInstall(params);
}

/**
 * Customer install-trip fees for additional venues standing up at conversion.
 * One trip fee per extra location; not covered by conversion credit.
 */
function additionalLocationInstallFee(params: VenuePricingParams): {
    locationCount: number;
    amount: number;
} {
    const locationCount = nonNegInt(params.additionalGoForwardLocations);
    if (locationCount <= 0) return { locationCount: 0, amount: 0 };
    return { locationCount, amount: money(discountedInstall(params) * locationCount) };
}

/** Uncredited install-trip cash at conversion: same-site return trip plus additional locations. */
function conversionTripFees(params: VenuePricingParams, expansionDeviceCount: number): number {
    const extraLocations = additionalLocationInstallFee(params);
    return money(returnTripInstallFee(params, expansionDeviceCount) + extraLocations.amount);
}

/** Months of Venue billing that fall in the first 12 months after install (pilot occupies early days). */
export function venueMonthsInFirstYear(pilotDurationDays: number): number {
    const days = Math.max(0, pilotDurationDays);
    if (days <= 0) return 12;
    // Count pilot length in whole months (30-day months), then the rest of a 12-month year-one window.
    // A 60-day package → 2 pilot months → 10 months of Venue in year one.
    const pilotMonths = Math.ceil(days / 30);
    return Math.max(0, 12 - pilotMonths);
}

type ConversionSlice = {
    installBefore: number;
    purchaseBefore: number;
    softwareBefore: number;
    leaseBefore: number;
    installAfter: number;
    purchaseAfter: number;
    softwareAfter: number;
    leaseAfter: number;
    creditApplied: number;
    creditLeftover: number;
    totalBefore: number;
    totalAfter: number;
};

function computeConversionForDevices(
    params: VenuePricingParams,
    deviceCount: number,
    options: { chargeInstall: boolean; applyCredit: boolean }
): ConversionSlice {
    const devices = nonNegInt(deviceCount);
    const installBefore = options.chargeInstall ? discountedInstall(params) : 0;
    const purchaseBefore = params.hardwareMode === 'purchase' ? money(discountedPurchase(params) * devices) : 0;
    const softwareBefore = money(discountedSoftwareContractTerm(params) * devices);
    const leaseBefore = params.hardwareMode === 'lease' ? money(discountedLeaseContractTerm(params) * devices) : 0;

    const totalBefore = money(installBefore + purchaseBefore + softwareBefore + leaseBefore);

    if (!options.applyCredit || params.conversionCredit <= 0) {
        return {
            installBefore,
            purchaseBefore,
            softwareBefore,
            leaseBefore,
            installAfter: installBefore,
            purchaseAfter: purchaseBefore,
            softwareAfter: softwareBefore,
            leaseAfter: leaseBefore,
            creditApplied: 0,
            creditLeftover: 0,
            totalBefore,
            totalAfter: totalBefore,
        };
    }

    const { afterCredit, creditApplied, creditLeftover } = applyCreditInOrder(
        [
            { key: 'install', amount: installBefore },
            { key: 'purchase', amount: purchaseBefore },
            { key: 'software', amount: softwareBefore },
            { key: 'lease', amount: leaseBefore },
        ],
        params.conversionCredit
    );

    const installAfter = afterCredit.install ?? 0;
    const purchaseAfter = afterCredit.purchase ?? 0;
    const softwareAfter = afterCredit.software ?? 0;
    const leaseAfter = afterCredit.lease ?? 0;

    return {
        installBefore,
        purchaseBefore,
        softwareBefore,
        leaseBefore,
        installAfter,
        purchaseAfter,
        softwareAfter,
        leaseAfter,
        creditApplied,
        creditLeftover,
        totalBefore,
        totalAfter: money(installAfter + purchaseAfter + softwareAfter + leaseAfter),
    };
}

function mrrForDevices(params: VenuePricingParams, deviceCount: number): number {
    const devices = nonNegInt(deviceCount);
    const lease = params.hardwareMode === 'lease' ? money(discountedLease(params) * devices) : 0;
    const software = money(discountedSoftwareMonthlyEquivalent(params) * devices);
    return money(lease + software);
}

function arrForDevices(params: VenuePricingParams, deviceCount: number): number {
    return money(mrrForDevices(params, deviceCount) * 12);
}

function contractTermRecurringCash(params: VenuePricingParams, deviceCount: number): number {
    const devices = nonNegInt(deviceCount);
    if (devices === 0) return 0;
    const lease = params.hardwareMode === 'lease' ? money(discountedLeaseContractTerm(params) * devices) : 0;
    const software = money(discountedSoftwareContractTerm(params) * devices);
    return money(lease + software);
}

function yearTwoCustomerTotal(params: VenuePricingParams, deviceCount: number): number {
    return contractTermRecurringCash(params, deviceCount);
}

function watchlyLaborCost(params: VenuePricingParams, devices: number): number {
    return money(params.deviceInstallationLaborCost * nonNegInt(devices));
}

function grossDeploySpend(params: VenuePricingParams, goForward: number, pilot: number): number {
    const go = nonNegInt(goForward);
    const pi = nonNegInt(pilot);
    const extras = Math.max(0, go - pi);
    const base = money((params.deviceHardwareCost + params.deviceInstallationLaborCost) * go);
    if (extras > 0 && params.expansionInstallTiming === 'return_trip') {
        // Return trip: labor again on extras only (hardware already counted once in base).
        return money(base + params.deviceInstallationLaborCost * extras);
    }
    return base;
}

/**
 * Months until cumulative cash from t=0 covers gross deploy spend (convert-and-expand path).
 * Cash: pilot package, then the 12-month conversion invoice after credit, then each
 * subsequent 12-month contract term at the conversion anniversary.
 */
function computeBreakEvenFromStart(params: VenuePricingParams, goForward: number): BreakEven {
    const spend = grossDeploySpend(params, goForward, params.pilotDeviceCount);
    if (spend <= 0) return { kind: 'immediate' };

    const pilotCash = money(Math.max(0, params.pilotPackagePrice));
    const conv = computeConversionForDevices(params, goForward, {
        chargeInstall: params.pilotDurationDays <= 0,
        applyCredit: params.pilotDurationDays > 0 && params.conversionCredit > 0,
    });
    const expansion = Math.max(0, nonNegInt(goForward) - nonNegInt(params.pilotDeviceCount));
    const tripFee = conversionTripFees(params, expansion);

    let cumulative = money(pilotCash + conv.totalAfter + tripFee);
    if (cumulative >= spend) return { kind: 'immediate' };

    const pilotMonthsApprox = params.pilotDurationDays > 0 ? Math.ceil(params.pilotDurationDays / 30) : 0;
    const termRenewal = contractTermRecurringCash(params, goForward);

    if (termRenewal <= 0) {
        return { kind: 'never' };
    }

    for (let m = 1; m <= 120; m++) {
        if (m % CONTRACT_TERM_MONTHS === 0) {
            cumulative = money(cumulative + termRenewal);
        }
        if (cumulative >= spend) {
            return { kind: 'months', monthsAfterStart: Math.ceil(pilotMonthsApprox) + m };
        }
    }

    return { kind: 'never' };
}

function lineItem(
    label: string,
    quantity: number,
    listUnit: number,
    discountFraction: number,
    amountBeforeCredit: number,
    amountAfterCredit: number
): VenueLineItem {
    return {
        label,
        quantity,
        listUnit,
        discountFraction,
        amountBeforeCredit: money(amountBeforeCredit),
        amountAfterCredit: money(amountAfterCredit),
    };
}

function leaseTermLabel(devices: number, expansion: number): string {
    if (expansion > 0) {
        return `Hardware lease, 12 months (${devices - expansion} converting + ${expansion} additional)`;
    }
    return 'Hardware lease (12 months)';
}

function appendUncreditedInstallLineItems(
    items: VenueLineItem[],
    params: VenuePricingParams,
    expansionDeviceCount: number
): number {
    let total = 0;
    const returnTrip = returnTripInstallFee(params, expansionDeviceCount);
    if (returnTrip > 0) {
        items.push(
            lineItem(
                'Installation (return trip)',
                1,
                params.installationFeePerTrip,
                params.installationDiscountFraction,
                returnTrip,
                returnTrip
            )
        );
        total = money(total + returnTrip);
    }
    const extraLocations = additionalLocationInstallFee(params);
    if (extraLocations.amount > 0) {
        items.push(
            lineItem(
                'Installation (additional locations)',
                extraLocations.locationCount,
                params.installationFeePerTrip,
                params.installationDiscountFraction,
                extraLocations.amount,
                extraLocations.amount
            )
        );
        total = money(total + extraLocations.amount);
    }
    return total;
}

function invoiceTotal(items: VenueLineItem[]): number {
    return money(items.reduce((sum, item) => sum + item.amountAfterCredit, 0));
}

function buildSignupInvoice(
    params: VenuePricingParams,
    goForward: number,
    conversion: ConversionSlice,
    isSteadyState: boolean
): CustomerInvoice {
    const items: VenueLineItem[] = [];
    const devices = nonNegInt(goForward);

    if (!isSteadyState) {
        if (params.pilotPackagePrice > 0) {
            items.push(
                lineItem(
                    'Pilot package',
                    1,
                    params.pilotPackagePrice,
                    0,
                    params.pilotPackagePrice,
                    params.pilotPackagePrice
                )
            );
        }
        return {
            id: 'signup',
            title: 'Pilot invoice',
            description:
                'Due at initial pilot signup. The package covers loaned hardware, the first install trip, and software access during the pilot window.',
            lineItems: items,
            creditApplied: 0,
            creditLeftover: 0,
            total: invoiceTotal(items),
        };
    }

    if (conversion.installBefore > 0) {
        items.push(
            lineItem(
                'Installation (install trip)',
                1,
                params.installationFeePerTrip,
                params.installationDiscountFraction,
                conversion.installBefore,
                conversion.installAfter
            )
        );
    }
    if (params.hardwareMode === 'purchase' && devices > 0) {
        items.push(
            lineItem(
                'Hardware purchase',
                devices,
                params.hardwarePurchase,
                params.hardwareDiscountFraction,
                conversion.purchaseBefore,
                conversion.purchaseAfter
            )
        );
    }
    if (params.hardwareMode === 'lease' && devices > 0) {
        items.push(
            lineItem(
                'Hardware lease (12 months)',
                devices,
                leaseContractTermListUnit(params),
                params.hardwareDiscountFraction,
                conversion.leaseBefore,
                conversion.leaseAfter
            )
        );
    }
    if (devices > 0) {
        items.push(
            lineItem(
                'Software subscription (12 months)',
                devices,
                softwareContractTermListUnit(params),
                params.softwareDiscountFraction,
                conversion.softwareBefore,
                conversion.softwareAfter
            )
        );
    }

    appendUncreditedInstallLineItems(items, params, Math.max(0, devices - nonNegInt(params.pilotDeviceCount)));

    return {
        id: 'signup',
        title: 'Signup invoice',
        description:
            'Due at initial signup. Includes the install trip, hardware (purchase or 12-month lease), and the first 12-month software term.',
        lineItems: items,
        creditApplied: 0,
        creditLeftover: 0,
        total: invoiceTotal(items),
    };
}

function buildConversionInvoice(
    params: VenuePricingParams,
    goForward: number,
    expansion: number,
    conversion: ConversionSlice
): CustomerInvoice {
    const items: VenueLineItem[] = [];
    const devices = nonNegInt(goForward);

    if (params.hardwareMode === 'purchase' && devices > 0) {
        items.push(
            lineItem(
                expansion > 0
                    ? `Hardware purchase (${devices - expansion} converting + ${expansion} additional)`
                    : 'Hardware purchase',
                devices,
                params.hardwarePurchase,
                params.hardwareDiscountFraction,
                conversion.purchaseBefore,
                conversion.purchaseAfter
            )
        );
    }
    if (params.hardwareMode === 'lease' && devices > 0) {
        items.push(
            lineItem(
                leaseTermLabel(devices, expansion),
                devices,
                leaseContractTermListUnit(params),
                params.hardwareDiscountFraction,
                conversion.leaseBefore,
                conversion.leaseAfter
            )
        );
    }
    if (devices > 0) {
        items.push(
            lineItem(
                'Software subscription (12 months)',
                devices,
                softwareContractTermListUnit(params),
                params.softwareDiscountFraction,
                conversion.softwareBefore,
                conversion.softwareAfter
            )
        );
    }
    const uncreditedInstall = appendUncreditedInstallLineItems(items, params, expansion);

    const creditedTotal = money(
        conversion.purchaseAfter + conversion.softwareAfter + conversion.leaseAfter + uncreditedInstall
    );

    return {
        id: 'conversion',
        title: 'Conversion invoice - Year 1',
        description:
            'Due when the pilot ends. Starts a regular 12-month Venue contract: hardware lease for 12 months if leasing (or hardware purchase if buying), plus software for the term. Conversion credit does not cover return-trip or extra-location install-trip fees.',
        lineItems: items,
        creditApplied: conversion.creditApplied,
        creditLeftover: conversion.creditLeftover,
        total: creditedTotal,
    };
}

function buildOngoingInvoice(params: VenuePricingParams, goForward: number): CustomerInvoice {
    const items: VenueLineItem[] = [];
    const devices = nonNegInt(goForward);

    if (params.hardwareMode === 'lease' && devices > 0) {
        const lease = money(discountedLeaseContractTerm(params) * devices);
        items.push(
            lineItem(
                'Hardware lease (12 months)',
                devices,
                leaseContractTermListUnit(params),
                params.hardwareDiscountFraction,
                lease,
                lease
            )
        );
    }
    if (devices > 0) {
        const software = money(discountedSoftwareContractTerm(params) * devices);
        items.push(
            lineItem(
                'Software subscription (12 months)',
                devices,
                softwareContractTermListUnit(params),
                params.softwareDiscountFraction,
                software,
                software
            )
        );
    }

    return {
        id: 'ongoing',
        title: 'Recurring invoice - Year 2+',
        description:
            'The next 12-month contract term after conversion, at go-forward device count. No package, install, or hardware purchase.',
        lineItems: items,
        creditApplied: 0,
        creditLeftover: 0,
        total: invoiceTotal(items),
    };
}

function scenarioWalkAway(params: VenuePricingParams): VenueScenarioColumn {
    const labor = watchlyLaborCost(params, params.pilotDeviceCount);
    // Hardware returns; Watchly keeps package cash and loses sunk labor.
    const packageCash = money(Math.max(0, params.pilotPackagePrice));
    return {
        id: 'walk_away',
        label: 'Walk away after pilot',
        customerCashThroughYearOne: packageCash,
        watchlyNetCashThroughYearOne: money(packageCash - labor),
        mrrAfterDecision: 0,
        deviceCount: 0,
    };
}

function scenarioConvert(
    params: VenuePricingParams,
    deviceCount: number,
    label: string,
    id: 'convert' | 'convert_and_expand'
): VenueScenarioColumn {
    const chargeInstall = params.pilotDurationDays <= 0;
    const applyCredit = params.pilotDurationDays > 0 && params.conversionCredit > 0;
    const conversion = computeConversionForDevices(params, deviceCount, {
        chargeInstall,
        applyCredit,
    });

    const packageCash = money(Math.max(0, params.pilotPackagePrice));
    const extras = Math.max(0, nonNegInt(deviceCount) - nonNegInt(params.pilotDeviceCount));
    const tripFee = conversionTripFees(params, extras);
    const customerY1 = money(packageCash + conversion.totalAfter + tripFee);

    const go = nonNegInt(deviceCount);
    const pilot = nonNegInt(params.pilotDeviceCount);
    let watchlySpend = money((params.deviceHardwareCost + params.deviceInstallationLaborCost) * go);
    if (extras > 0 && params.expansionInstallTiming === 'return_trip') {
        watchlySpend = money(watchlySpend + params.deviceInstallationLaborCost * extras);
    }

    // On convert, hardware stays in the field (customer purchase or Watchly-owned lease).
    // Net = customer cash − deploy spend (simplified P&L through year one).
    const watchlyNet = money(customerY1 - watchlySpend);

    return {
        id,
        label,
        customerCashThroughYearOne: customerY1,
        watchlyNetCashThroughYearOne: watchlyNet,
        mrrAfterDecision: mrrForDevices(params, deviceCount),
        deviceCount: go,
    };
}

/**
 * Cumulative Watchly net profit over time for the convert-and-expand (go-forward) path.
 *
 * Cash in: pilot package, conversion invoice after credit (the first 12-month contract),
 * return-trip install fee, then each subsequent 12-month term at the conversion anniversary.
 * Cash out: hardware + labor when devices are deployed (extras on a return trip also incur a
 * second labor hit). Horizon defaults to 60 months (5 years).
 */
export function computeWatchlyNetProfitSeries(
    overrides: Partial<VenuePricingParams> = {},
    horizonMonths: number = NET_PROFIT_HORIZON_MONTHS
): WatchlyNetProfitPoint[] {
    const params: VenuePricingParams = { ...DEFAULT_VENUE_PRICING, ...overrides };
    const pilot = nonNegInt(params.pilotDeviceCount);
    const goForward = Math.max(pilot, nonNegInt(params.goForwardDeviceCount));
    const expansion = Math.max(0, goForward - pilot);
    const effective: VenuePricingParams = {
        ...params,
        pilotDeviceCount: pilot,
        goForwardDeviceCount: goForward,
        hardwareDiscountFraction: clampFraction(params.hardwareDiscountFraction),
        installationDiscountFraction: clampFraction(params.installationDiscountFraction),
        softwareDiscountFraction: clampFraction(params.softwareDiscountFraction),
        additionalGoForwardLocations: nonNegInt(params.additionalGoForwardLocations),
    };

    const isSteadyState = effective.pilotDurationDays <= 0;
    const pilotMonths = isSteadyState ? 0 : Math.ceil(effective.pilotDurationDays / 30);
    const conversionMonth = pilotMonths;

    const chargeInstall = isSteadyState;
    const applyCredit = !isSteadyState && effective.conversionCredit > 0;
    const conversion = computeConversionForDevices(effective, goForward, {
        chargeInstall,
        applyCredit,
    });
    const tripFee = conversionTripFees(effective, expansion);

    const deployCostPerDevice = money(effective.deviceHardwareCost + effective.deviceInstallationLaborCost);
    const pilotDeployCost =
        effective.expansionInstallTiming === 'during_pilot'
            ? money(deployCostPerDevice * goForward)
            : money(deployCostPerDevice * pilot);
    const returnDeployCost =
        expansion > 0 && effective.expansionInstallTiming === 'return_trip'
            ? money(deployCostPerDevice * expansion + effective.deviceInstallationLaborCost * expansion)
            : 0;

    const termRenewal = contractTermRecurringCash(effective, goForward);

    const packageCash = money(Math.max(0, effective.pilotPackagePrice));
    const horizon = Math.max(1, Math.floor(horizonMonths));

    let cumulative = 0;
    const points: WatchlyNetProfitPoint[] = [];

    for (let m = 0; m <= horizon; m++) {
        if (m === 0) {
            cumulative = money(cumulative + packageCash - pilotDeployCost);
        }

        if (m === conversionMonth) {
            cumulative = money(cumulative + conversion.totalAfter + tripFee - returnDeployCost);
        }

        // Later 12-month terms land at conversion + 12, 24, … . The first term is already in
        // the conversion invoice. Skip month `horizon` so we don't bill a term that starts after
        // the plotted window.
        if (m > conversionMonth && m < horizon) {
            const monthsSinceConversion = m - conversionMonth;
            if (monthsSinceConversion % CONTRACT_TERM_MONTHS === 0) {
                cumulative = money(cumulative + termRenewal);
            }
        }

        points.push({ month: m, netProfit: cumulative });
    }

    return points;
}

export function computeVenuePricing(overrides: Partial<VenuePricingParams> = {}): VenuePricingBreakdown {
    const params: VenuePricingParams = { ...DEFAULT_VENUE_PRICING, ...overrides };

    const pilot = nonNegInt(params.pilotDeviceCount);
    const goForward = Math.max(pilot, nonNegInt(params.goForwardDeviceCount));
    const expansion = Math.max(0, goForward - pilot);

    const effectiveParams: VenuePricingParams = {
        ...params,
        pilotDeviceCount: pilot,
        goForwardDeviceCount: goForward,
        hardwareDiscountFraction: clampFraction(params.hardwareDiscountFraction),
        installationDiscountFraction: clampFraction(params.installationDiscountFraction),
        softwareDiscountFraction: clampFraction(params.softwareDiscountFraction),
        additionalGoForwardLocations: nonNegInt(params.additionalGoForwardLocations),
    };

    const isSteadyState = effectiveParams.pilotDurationDays <= 0;
    const chargeInstall = isSteadyState;
    const applyCredit = !isSteadyState && effectiveParams.conversionCredit > 0;

    const conversion = computeConversionForDevices(effectiveParams, goForward, {
        chargeInstall,
        applyCredit,
    });

    const leasePer = discountedLease(effectiveParams);
    const purchasePer = discountedPurchase(effectiveParams);
    const install = discountedInstall(effectiveParams);
    const softwarePeriod = discountedSoftwarePeriod(effectiveParams);
    const softwareMonthlyEq = discountedSoftwareMonthlyEquivalent(effectiveParams);

    const spend = grossDeploySpend(effectiveParams, goForward, pilot);

    const expansionTripDue = conversionTripFees(effectiveParams, expansion);

    // Capital still tied up after the pilot: deploy spend minus the package fee and conversion
    // cash that offsets hardware/install. Lease and software do not offset capital.
    const packageCash = money(Math.max(0, effectiveParams.pilotPackagePrice));
    const capitalOffsetting = money(
        packageCash + conversion.installAfter + conversion.purchaseAfter + expansionTripDue
    );
    const upFrontCapital = money(spend - capitalOffsetting);

    const capitalTiedUpDuringPilot = Math.max(
        0,
        money((effectiveParams.deviceHardwareCost + effectiveParams.deviceInstallationLaborCost) * pilot - packageCash)
    );

    const mrr = mrrForDevices(effectiveParams, goForward);
    const arr = arrForDevices(effectiveParams, goForward);

    // Expansion hardware due: when purchasing, extras are included in go-forward purchase line.
    // Surface the delta for the invoice "by phase" view.
    const expansionHardwareDue =
        expansion > 0 && effectiveParams.hardwareMode === 'purchase'
            ? money(purchasePer * expansion)
            : expansion > 0 && effectiveParams.hardwareMode === 'lease'
              ? money(discountedLeaseContractTerm(effectiveParams) * expansion)
              : 0;

    const breakEven = computeBreakEvenFromStart(effectiveParams, goForward);

    // Incremental next screen on a later return trip: hardware + install-trip fee + software.
    const nextCustomerUpFront = money((effectiveParams.hardwareMode === 'purchase' ? purchasePer : 0) + install);
    const nextCustomerMonthly =
        effectiveParams.hardwareMode === 'lease' ? money(leasePer + softwareMonthlyEq) : softwareMonthlyEq;
    const nextWatchlyCost = money(effectiveParams.deviceHardwareCost + effectiveParams.deviceInstallationLaborCost);
    let monthsToRecover: BreakEven;
    if (nextWatchlyCost <= 0) {
        monthsToRecover = { kind: 'immediate' };
    } else if (nextCustomerUpFront >= nextWatchlyCost) {
        monthsToRecover = { kind: 'immediate' };
    } else if (nextCustomerMonthly <= 0) {
        monthsToRecover = { kind: 'never' };
    } else {
        const remaining = nextWatchlyCost - nextCustomerUpFront;
        monthsToRecover = {
            kind: 'months',
            monthsAfterStart: Math.ceil(remaining / nextCustomerMonthly),
        };
    }

    const venueMonths = venueMonthsInFirstYear(effectiveParams.pilotDurationDays);

    // First paid year: pilot package (if any) plus the 12-month contract invoiced at conversion
    // or signup. Remaining months after a short calendar year-one window are not billed extra —
    // that first contract already covers 12 months of Venue after the pilot.
    const yearOne = money((isSteadyState ? 0 : packageCash) + conversion.totalAfter + expansionTripDue);

    const yearTwo = yearTwoCustomerTotal(effectiveParams, goForward);

    const invoices = {
        signup: buildSignupInvoice(effectiveParams, goForward, conversion, isSteadyState),
        conversion: isSteadyState ? null : buildConversionInvoice(effectiveParams, goForward, expansion, conversion),
        ongoing: buildOngoingInvoice(effectiveParams, goForward),
    };

    const watchlyNetProfitSeries = computeWatchlyNetProfitSeries(effectiveParams, NET_PROFIT_HORIZON_MONTHS);

    const scenarios: VenueScenarioColumn[] = isSteadyState
        ? [scenarioConvert(effectiveParams, goForward, 'Steady-state (go-forward)', 'convert_and_expand')]
        : [
              scenarioWalkAway(effectiveParams),
              scenarioConvert(effectiveParams, pilot, 'Convert at pilot count', 'convert'),
              scenarioConvert(effectiveParams, goForward, 'Convert and expand', 'convert_and_expand'),
          ];

    const recurringMonthly = mrr;
    const recurringSoftwarePeriod = money(softwarePeriod * goForward);

    return {
        pilotDeviceCount: pilot,
        goForwardDeviceCount: goForward,
        expansionDeviceCount: expansion,
        venueMonthsInYearOne: venueMonths,

        discountedLeasePerDevice: leasePer,
        discountedPurchasePerDevice: purchasePer,
        discountedInstall: install,
        discountedSoftwarePeriodPerDevice: softwarePeriod,
        discountedSoftwareMonthlyEquivalentPerDevice: softwareMonthlyEq,

        grossDeploySpend: spend,
        upFrontCapitalStillOnWatchly: Math.max(0, upFrontCapital),
        capitalTiedUpDuringPilot,
        breakEvenFromStart: breakEven,

        mrr,
        arr,

        incrementalNextScreen: {
            customerUpFront: nextCustomerUpFront,
            customerMonthlyRecurring: nextCustomerMonthly,
            watchlyCost: nextWatchlyCost,
            monthsToRecover,
        },

        pilotPackageDue: packageCash,
        conversionBeforeCredit: {
            install: conversion.installBefore,
            hardwarePurchase: conversion.purchaseBefore,
            firstSoftwarePeriod: conversion.softwareBefore,
            firstLeaseMonth: conversion.leaseBefore,
            total: conversion.totalBefore,
        },
        conversionAfterCredit: {
            install: conversion.installAfter,
            hardwarePurchase: conversion.purchaseAfter,
            firstSoftwarePeriod: conversion.softwareAfter,
            firstLeaseMonth: conversion.leaseAfter,
            total: conversion.totalAfter,
            creditApplied: conversion.creditApplied,
            creditLeftover: conversion.creditLeftover,
        },
        expansionHardwareDue,
        expansionInstallTripDue: expansionTripDue,
        recurringMonthly,
        recurringSoftwarePeriod,
        recurringSoftwarePeriodLabel: effectiveParams.softwareCadence,

        invoices,

        yearOneCustomerTotal: yearOne,
        yearTwoCustomerTotal: yearTwo,

        scenarios,
        watchlyNetProfitSeries,
    };
}
